import { Mediator, Router, app, eData, payload } from "modular-openscriptjs";
import {
    eParse,
    inform,
    processStart,
    processEnd,
    authenticated,
    runAfterAuth,
    oneOf,
    redirect,
    empty,
    saveUser,
} from "../helpers";
import { STRINGS, api, gc } from "../declarations";
import { analyze } from "../analytics";
import { routes } from "../backendRoutes";
import { firebaseAuth, googleProvider } from "../firebase";
import { signInWithPopup } from "firebase/auth";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { activateEchoListeners } from "../socket-config";
import { $e } from "../ojs.events";
import System from "./System";

export class Auth extends Mediator {
    $$user = {
        authenticated: async () => {
            if (typeof activateEchoListeners === "function") {
                activateEchoListeners();
            }
            runAfterAuth();
            await this.redirectIfNeeded();
        },

        needs: {
            googleLogin: async () => {
                processStart({ title: "Signing you in with Google" });

                try {
                    let idToken;

                    if (Capacitor.isNativePlatform()) {
                        await FirebaseAuthentication.signInWithGoogle();

                        const result = await FirebaseAuthentication.getIdToken({
                            forceRefresh: true,
                        });

                        idToken = result.token;
                    } else {
                        const result = await signInWithPopup(
                            firebaseAuth,
                            googleProvider,
                        );

                        idToken = await result.user.getIdToken();
                    }

                    if (!idToken) {
                        throw new Error("No ID Token received");
                    }

                    let response = await api.post(routes.firebaseLogin, {
                        id_token: idToken,
                    });

                    processEnd();

                    if (!response) return;

                    const token = response.data.token;
                    await Preferences.set({ key: "auth_token", value: token });
                    api.token = token;

                    await new System().loadAppConfigs();

                    if (gc.auth.value) {
                        analyze("login", { method: "google" });
                        this.send($e.user.authenticated);
                    } else {
                        processEnd();
                        inform(
                            "You are not authorized to access Carata Rider",
                            STRINGS.WARNING,
                        );
                    }
                } catch (error) {
                    processEnd();
                    inform("Google Sign-In failed", STRINGS.ERROR);
                }
            },

            emailLogin: (ed, event) => {
                this.doEmailLogin(ed, event);
            },

            logout: () => {
                this.logout();
            },
        },

        has: {
            emailLoginError: (ed, event) => {
                let data = eParse(ed);
                inform(data.message.notice, STRINGS.ERROR);
            },
        },
    };

    $$system = {
        booted: async () => {
            if (!gc.auth.value) {
                this.broadcast($e.user.loggedOut);
                return;
            }

            this.broadcast($e.user.authenticated);
        },
    };

    async doEmailLogin(ed, event) {
        let data = eParse(ed);

        let email = data.message.email;
        let password = data.message.password;
        let rememberMe = data.message.rememberMe;

        processStart({ title: "Logging you In" });

        let response = await api.post(routes.login, {
            email,
            password,
            remember: rememberMe,
        });

        processEnd();

        if (!response) return;

        const token = response.data.token;
        await Preferences.set({ key: "auth_token", value: token });
        api.token = token;

        await new System().loadAppConfigs();

        if (gc.auth.value) {
            analyze("login", { method: "email" });
            this.send($e.user.authenticated);

            return;
        } else {
            processEnd();
            inform(
                "You are not authorized to access Carata Rider",
                STRINGS.WARNING,
            );
        }

        this.send($e.user.has.emailLoginError);
    }

    async logout() {
        processStart({ title: "Logging you out" });
        await api.post(routes.logout);
        processEnd();

        await Preferences.remove({ key: "auth_token" });
        await saveUser(null);
        api.token = null;

        redirect().to("home");

        setTimeout(() => {
            this.send($e.system.needs.configReload);
        }, 1000);
    }

    async redirectIfNeeded() {
        const { value: url } = await Preferences.get({
            key: "redirect-url-after-auth",
        });
        await Preferences.remove({ key: "redirect-url-after-auth" });

        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            window.location.href = url;
        } else {
            redirect().to(url);
        }
    }
}
